import React, { useState } from "react";
import { OrderProductModel } from "@admin/domain/orders/models/OrderProductModel";
import { EditOrderData } from "@admin/domain/orders/dto/EditOrderData";
import formatMoney from "@admin/domain/orders/formatMoney";

type Props = {
    order: EditOrderData;
};

export default function Totals({ order }: Props) {
    const [hover, setHover] = useState(false);
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(order.delivery_cost / 100);
    const [orderAmount] = useState(order.total_amount - order.delivery_cost);
    const handleKeyDown = async (e: React.KeyboardEvent) => {
        if (e.key === `Enter`) {
            try {
                const response = await fetch(`/api/v1/orders/${order.id}/change-delivery-cost`, {
                    method: `POST`,
                    headers: {
                        "Content-Type": `application/json`,
                    },
                    body: JSON.stringify({ delivery_cost: value }),
                });

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(`Ошибка сохранения`);
                }

                order.delivery_cost = value * 100;
                order.total_amount = orderAmount + data.payload.shipping_info.delivery_cost;

                setEditing(false);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error(err);
            }
        }
    };

    const [isPayable, setIsPayable] = useState(order.is_payable ?? false);
    const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        setIsPayable(checked);

        try {
            await fetch(`/api/v1/orders/${order.id}/set-order-payable`, {
                method: `POST`,
                headers: {
                    "Content-Type": `application/json`,
                },
                body: JSON.stringify({ is_payable: checked }),
            });
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(`Ошибка при обновлении:`, err);
        }
    };

    return (
        <div className="cursor-default">
            <h4 className="mb-5">Итоговая стоимость заказа</h4>
            <div className="mb-5">
                <span className="fw-bolder">Общее кол-во товаров: </span>
                <span>
                    {order.products.reduce(
                        (total: number, orderProduct: OrderProductModel): number => total + orderProduct.quantity,
                        0,
                    )}
                </span>
            </div>
            {order.is_child && order.creator_discount > 0 && (
                <div className="mb-5">
                    <span className="fw-bolder">Скидка консолидатора: </span>
                    <span>{formatMoney(order, `creator_discount`)}</span>
                </div>
            )}
            {order.is_child && order.target_discount > 0 && (
                <div className="mb-5">
                    <span className="fw-bolder">Скидка получателя: </span>
                    <span>{formatMoney(order, `target_discount`)}</span>
                </div>
            )}
            {!order.is_child && order.target_discount + order.creator_discount > 0 && (
                <div className="mb-5">
                    <span className="fw-bolder">Скидка дистрибьютора:{` `}</span>
                    <span>
                        {(order.target_discount + order.creator_discount) / 100}
                        {` `}
                        {order.currency_symbol}
                    </span>
                </div>
            )}
            {order.promo_code_discount > 0 && (
                <div className="mb-5">
                    <span className="fw-bolder">Промокод {order.promo_code} скидка: </span>
                    <span>{formatMoney(order, `promo_code_discount`)}</span>
                </div>
            )}
            {order.simple_promotion_discount > 0 && (
                <div className="mb-5">
                    <span className="fw-bolder">Скидка по простым акциям:{` `}</span>
                    <span>{formatMoney(order, `simple_promotion_discount`)}</span>
                </div>
            )}
            {order.complex_promotion_discount > 0 && (
                <div className="mb-5">
                    <span className="fw-bolder">Скидка по сложным акциям:{` `}</span>
                    <span>{formatMoney(order, `complex_promotion_discount`)}</span>
                </div>
            )}
            {order.electronic_gift_certificate_discount > 0 && (
                <div className="mb-5">
                    <span className="fw-bolder">Сертификат {order.electronic_gift_certificate_code}: </span>
                    <span>{formatMoney(order, `electronic_gift_certificate_discount`)}</span>
                </div>
            )}
            {!order.is_child && (
                <div className="mb-5">
                    <span className="fw-bolder">Доставка: </span>
                    {editing ? (
                        <input
                            type="number"
                            value={value}
                            autoFocus
                            onChange={(e) => setValue(Number(e.target.value))}
                            onKeyDown={handleKeyDown}
                            onBlur={() => setEditing(false)} // закрыть если ушли фокусом
                            style={{ padding: `2px 4px`, borderRadius: `4px` }}
                            id="input-delivery-cost"
                        />
                    ) : (
                        <span
                            style={{
                                backgroundColor: hover ? `#f0f0f0` : `transparent`,
                                cursor: `pointer`,
                                borderRadius: `4px`,
                                padding: `2px 4px`,
                            }}
                            onMouseEnter={() => setHover(true)}
                            onMouseLeave={() => setHover(false)}
                            onClick={() => setEditing(true)}
                        >
                            {order.delivery_cost > 0 ? formatMoney(order, `delivery_cost`) : `бесплатно`}
                        </span>
                    )}
                </div>
            )}
            <div className="mb-5">
                <span className="fw-bolder">Итоговая стоимость заказа: </span>
                <span>{formatMoney(order, `total_amount`)}</span>
            </div>
            <div className="mb-5">
                <span className="fw-bolder">Баллы: </span>
                <span>{order.total_points / 100}</span>
            </div>
            <div className="mb-5">
                <label className="flex items-center space-x-2 cursor-pointer">
                    <input type="checkbox" checked={isPayable} onChange={handleChange}
                        className="form-checkbox cursor-pointer" id="set-order-payable" />
                    <span className="ps-3">Разрешить оплату</span>
                </label>
            </div>
        </div>
    );
}
